/**
 * Condition objects — the app's view of the shared judgment core.
 *
 * PRD: docs/9-exploration/CONDITION-MONITORING-AND-DECISION-EXPIRY-PRD.md (C1–C3).
 * Schema + RPCs: supabase/migrations/699_condition_objects.sql.
 * IO: src/lib/conditionObjectsService.ts.
 *
 * The judgment itself — is this threshold breached, is this claim stale, which
 * transitions are legal, what does the rollup say — lives in
 * `supabase/functions/_shared/conditionHealthCore.ts`, because the ambient
 * watcher needs exactly the same answers and a second implementation would
 * drift (the cassandraAnalyzer.ts / reviewDueAnalyzer.ts precedent: the app
 * imports the core, it never re-derives it).
 *
 * This module is the app-side surface of that core plus the one thing the core
 * cannot own: turning a `condition_health` database row — whose `binding` is
 * untyped jsonb — into the typed shape the core takes.
 */

export {
  CONDITION_KIND_LABELS,
  CONDITION_STALE_AFTER_DAYS,
  CONDITION_STATUS_LABELS,
  CONDITION_WATCH_MAX_FINDINGS_PER_RUN,
  canTransitionCondition,
  describeThreshold,
  detectConditionCandidates,
  isConditionLive,
  judgeCondition,
  rollUpConditions,
  thresholdHolds,
} from '../../supabase/functions/_shared/conditionHealthCore';

export type {
  ConditionHealth,
  ConditionHealthOptions,
  ConditionInput,
  ConditionKind,
  ConditionRollup,
  ConditionStatus,
  ConditionWatchDraft,
  ConditionWatchFindingType,
  ThresholdBinding,
  ThresholdComparator,
} from '../../supabase/functions/_shared/conditionHealthCore';

import type {
  ConditionInput,
  ConditionKind,
  ConditionStatus,
  ThresholdBinding,
  ThresholdComparator,
} from '../../supabase/functions/_shared/conditionHealthCore';

const COMPARATORS: readonly ThresholdComparator[] = ['lt', 'lte', 'gt', 'gte', 'eq', 'ne'];

/**
 * Read a `condition_objects.binding` jsonb value as a threshold, or null.
 *
 * Null on anything malformed rather than a guess: `judgeCondition` reports a
 * condition with no threshold as un-breached, which is the honest answer when
 * the bound cannot be read. Inventing a comparator would let the system assert
 * a claim it cannot check.
 */
export function parseThresholdBinding(binding: unknown): ThresholdBinding | null {
  if (!binding || typeof binding !== 'object') return null;
  const raw = binding as Record<string, unknown>;
  const comparator = raw.comparator;
  const value = raw.value;
  if (typeof comparator !== 'string' || !COMPARATORS.includes(comparator as ThresholdComparator)) {
    return null;
  }
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return {
    comparator: comparator as ThresholdComparator,
    value,
    unit: typeof raw.unit === 'string' ? raw.unit : undefined,
    sourceLabel: typeof raw.source_label === 'string' ? raw.source_label : undefined,
  };
}

/** The subset of a `condition_health` row the judgment core needs. */
export interface ConditionHealthRowLike {
  id: string;
  kind: string;
  status: string;
  statement: string;
  subject_kind: string;
  subject_title: string | null;
  item_id: string | null;
  binding: unknown;
  last_observed_value: number | string | null;
  last_observed_at: string | null;
  last_observed_basis: string | null;
  last_evaluated_at: string | null;
}

/**
 * Row → core input. `last_observed_value` arrives as a string from PostgREST
 * (numeric is serialized as text to preserve precision), so it is coerced here
 * once rather than at every call site.
 */
export function toConditionInput(row: ConditionHealthRowLike): ConditionInput {
  const observed =
    row.last_observed_value === null || row.last_observed_value === ''
      ? null
      : Number(row.last_observed_value);
  return {
    id: row.id,
    kind: row.kind as ConditionKind,
    status: row.status as ConditionStatus,
    statement: row.statement,
    subjectTitle: row.subject_title,
    subjectKind: row.subject_kind === 'decision' ? 'decision' : 'commitment',
    itemId: row.item_id,
    threshold: row.kind === 'threshold' ? parseThresholdBinding(row.binding) : null,
    lastObservedValue: observed !== null && Number.isFinite(observed) ? observed : null,
    lastObservedAt: row.last_observed_at,
    lastObservedBasis: row.last_observed_basis,
    lastEvaluatedAt: row.last_evaluated_at,
  };
}
